import { useEffect, useState } from "react";

// The browser only finalizes LCP on the first input, so a candidate followed by
// this much quiet is treated as final. The cap bounds slow or blank loads.
const LCP_QUIET_WINDOW_MS = 2500;
const GATE_CAP_MS = 10_000;
const IDLE_TIMEOUT_MS = 2000;

let gate: Promise<void> | undefined;

function openAfterLcp(): Promise<void> {
  return new Promise((resolve) => {
    let quietTimer: number | undefined;
    let observer: PerformanceObserver | undefined;

    const open = () => {
      window.clearTimeout(capTimer);
      window.clearTimeout(quietTimer);
      observer?.disconnect();
      window.removeEventListener("pointerdown", open, true);
      window.removeEventListener("keydown", open, true);
      window.removeEventListener("load", open);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      resolve();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") open();
    };

    const capTimer = window.setTimeout(
      open,
      Math.max(0, GATE_CAP_MS - performance.now()),
    );
    if (document.visibilityState === "hidden") {
      open();
      return;
    }
    window.addEventListener("pointerdown", open, true);
    window.addEventListener("keydown", open, true);
    document.addEventListener("visibilitychange", onVisibilityChange);

    if (
      typeof PerformanceObserver === "undefined" ||
      !PerformanceObserver.supportedEntryTypes?.includes(
        "largest-contentful-paint",
      )
    ) {
      if (document.readyState === "complete") open();
      else window.addEventListener("load", open);
      return;
    }

    observer = new PerformanceObserver(() => {
      window.clearTimeout(quietTimer);
      quietTimer = window.setTimeout(open, LCP_QUIET_WINDOW_MS);
    });
    observer.observe({ type: "largest-contentful-paint", buffered: true });
  });
}

function whenIdle(task: () => void) {
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(task, { timeout: IDLE_TIMEOUT_MS });
  } else {
    setTimeout(task, 0);
  }
}

/** Runs `task` in an idle period once this page load's LCP has settled. */
export function runAfterLargestContentfulPaint(task: () => void): void {
  gate ??= openAfterLcp();
  void gate.then(() => whenIdle(task));
}

export function useAfterLargestContentfulPaint(): boolean {
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    let mounted = true;
    runAfterLargestContentfulPaint(() => {
      if (mounted) setSettled(true);
    });
    return () => {
      mounted = false;
    };
  }, []);

  return settled;
}
